/**
 * Pure per-tick logic for the multi-room drift-correction loop.
 *
 * Every DRIFT_TICK_INTERVAL_MS the playback hook measures how far the live
 * audio element has drifted from the server-issued segment timeline and picks
 * a correction (rate nudge / hard seek / nothing) via the shared player-core
 * policy. All decisions live here so they can be unit-tested without a DOM —
 * the hook is only a thin timer that applies the returned action.
 */

import type { DeviceSync } from "@workspace/api-client-react";
import {
  driftCorrectionAction,
  type DriftAction,
  type DriftCorrectionOptions,
  rebasedDriftSec,
  type SeekRebase,
} from "@workspace/player-core";
import {
  activeSegmentAt,
  channelDriftSec,
  channelTimeline,
  type TimelineAlignment,
  type TimelineChannel,
} from "./sync-group.ts";

/** Cadence of drift measurements while scheduled music is on air. */
export const DRIFT_TICK_INTERVAL_MS = 5_000;

/**
 * Playback-state snapshot deciding whether a drift tick may run. Correction
 * only makes sense while the music bus is playing scheduled music: interrupts
 * (ads/announcements), live broadcasts, and in-flight crossfades all detach
 * the audible position from the timeline on purpose, and an unsynced clock
 * would turn local skew into a bogus "drift".
 */
export interface DriftTickEligibility {
  /** The sync payload carries a music segment timeline. */
  hasTimeline: boolean;
  /** A server clock-offset estimate exists (see use-clock-offset). */
  clockSynced: boolean;
  /** PlayReason of the entry on the audio bus (null when idle). */
  reason: string | null;
  /** The on-air entry is a priority interrupt (ad/announcement/manual). */
  interrupt: boolean;
  isPlaying: boolean;
  broadcastActive: boolean;
  /** The mixer is mid-crossfade (two elements audible; rates must not move). */
  crossfading: boolean;
}

export function isDriftTickEligible(e: DriftTickEligibility): boolean {
  return (
    e.hasTimeline &&
    e.clockSynced &&
    e.reason === "music" &&
    !e.interrupt &&
    e.isPlaying &&
    !e.broadcastActive &&
    !e.crossfading
  );
}

export interface DriftTickInput {
  sync: DeviceSync | null;
  /** Timeline channel to measure against (music unless stated otherwise). */
  channelType?: TimelineChannel;
  /** Item id currently audible on the bus. */
  itemId: string | null | undefined;
  /** Actual playback position of the active element (seconds). */
  positionSec: number;
  /** Server-corrected wall clock (see use-clock-offset). */
  now: Date;
  /** Previous tick ended in a nudge (hysteresis carry-over). */
  wasNudging: boolean;
  /**
   * A deliberate operator seek. While it applies, drift is measured against it
   * instead of the projected timeline — otherwise the seek offset reads as
   * drift and the very next tick undoes the operator's action.
   */
  rebase?: SeekRebase | null;
  /**
   * Consecutive ticks (so far) on which the timeline's active segment was a
   * DIFFERENT item than the one on air. Carried by the caller between ticks;
   * the decision returns the updated count.
   */
  wrongItemTicks?: number;
  options?: DriftCorrectionOptions;
}

export interface DriftTickDecision {
  /**
   * Measured drift (actual − expected, seconds), or null when the timeline has
   * no active segment for the playing item (nothing to correct against).
   */
  driftSec: number | null;
  action: DriftAction;
  /** Next tick's `wasNudging` (mirror of action.nudging). */
  nudging: boolean;
  /**
   * The timeline HAS an active segment right now, but its item is not the one
   * on air. Position math is meaningless (driftSec stays null) and no nudge or
   * same-item seek can fix it — which is exactly why this signal exists:
   * without it, a player knocked off the shared timeline (a boundary advance,
   * a quick-play takeover ending) had NO recovery path at all. planDriftSeek's
   * 'rejoin' branch was reachable only via a seek action, a seek action needed
   * measured drift, and drift needed the items to already match. Observed
   * live: two grouped players diverged and stayed diverged for 47 minutes.
   */
  wrongItem: boolean;
  /** Next tick's `wrongItemTicks`. */
  wrongItemTicks: number;
}

/**
 * Wrong-item ticks before the loop escalates to a rejoin. Two ticks (~10s at
 * the 5s cadence) rather than one: near a track's natural end the timeline
 * advances to the next segment slightly before the local element finishes, and
 * rejoining on the first mismatched tick would hard-cut song tails that were
 * about to resolve themselves.
 */
export const REJOIN_AFTER_WRONG_ITEM_TICKS = 2;

const NO_ACTION: DriftAction = { kind: "none", nudging: false };
const SEEK_ACTION: DriftAction = { kind: "seek", nudging: false };

/** Decide the correction for one drift-loop tick. */
export function driftLoopTick(input: DriftTickInput): DriftTickDecision {
  const rebased = rebasedDriftSec(
    input.rebase,
    input.itemId,
    input.positionSec,
    input.now.getTime(),
  );
  if (rebased !== null) {
    const action = driftCorrectionAction(rebased, input.wasNudging, input.options);
    return {
      driftSec: rebased,
      action,
      nudging: action.nudging,
      wrongItem: false,
      wrongItemTicks: 0,
    };
  }

  // Distinguish "no active segment" (nothing to correct against — degrade to
  // the local walk) from "active segment, WRONG item" (recoverable, and only
  // recoverable through a rejoin).
  const timeline = channelTimeline(input.sync, input.channelType ?? "music");
  const active = input.itemId ? activeSegmentAt(timeline, input.now) : null;
  if (active && input.itemId && active.segment.itemId !== input.itemId) {
    const ticks = (input.wrongItemTicks ?? 0) + 1;
    if (ticks >= REJOIN_AFTER_WRONG_ITEM_TICKS) {
      // Escalate: the seek action routes through the hook's existing
      // alignment -> planDriftSeek path, whose 'rejoin' branch realigns the
      // rotation and advances. Keep the count: if the rejoin is deferred (an
      // interrupt owns the bus next), the next tick escalates again.
      return {
        driftSec: null,
        action: SEEK_ACTION,
        nudging: false,
        wrongItem: true,
        wrongItemTicks: ticks,
      };
    }
    return {
      driftSec: null,
      action: NO_ACTION,
      nudging: false,
      wrongItem: true,
      wrongItemTicks: ticks,
    };
  }

  const drift = channelDriftSec(
    input.sync,
    input.channelType ?? "music",
    input.itemId,
    input.positionSec,
    input.now,
  );
  if (drift === null) {
    return { driftSec: null, action: NO_ACTION, nudging: false, wrongItem: false, wrongItemTicks: 0 };
  }
  const action = driftCorrectionAction(drift, input.wasNudging, input.options);
  return {
    driftSec: drift,
    action,
    nudging: action.nudging,
    wrongItem: false,
    wrongItemTicks: 0,
  };
}

/** How a hard drift seek is executed against the live playback state. */
export type DriftSeekPlan =
  /** Same track, wrong position: snap the live element back. */
  | { kind: "seek-element"; seekSec: number }
  /**
   * The timeline moved on to another track — rejoin through the existing
   * alignment + advance path (never force state ad hoc).
   */
  | { kind: "rejoin" }
  /** No alignment, or queued interrupts own what plays next: do nothing. */
  | { kind: "none" };

/**
 * Resolve a "seek" drift action into what the hook should actually do, given
 * the current timeline alignment, the on-air item, and whether interrupts
 * are queued (an interrupt about to play must not be clobbered by a rejoin).
 */
export function planDriftSeek(args: {
  alignment: TimelineAlignment | null;
  currentItemId: string | null | undefined;
  queuedInterrupts: number;
}): DriftSeekPlan {
  if (!args.alignment) return { kind: "none" };
  if (args.alignment.segment.itemId === args.currentItemId) {
    return { kind: "seek-element", seekSec: args.alignment.seekSec };
  }
  if (args.queuedInterrupts === 0) return { kind: "rejoin" };
  return { kind: "none" };
}
